import { useCallback, useEffect, useRef, useState } from 'react';
import { chiffrer, type LigneCommande } from './gateway.supabase';
import { ErreurDeCommande } from './gateway.supabase';
import type { Devis } from './devis';

/**
 * Le devis du panier, tenu à jour.
 *
 * Il est redemandé à la base à chaque changement — une ligne ajoutée, la case
 * du tarif réduit cochée, un code saisi. C'est un aller-retour de plus, et
 * c'est le prix à payer pour que l'écran ne calcule plus rien : `quote_order`
 * est STABLE et ne prend aucun verrou, donc l'appeler souvent ne coûte rien à
 * la base.
 *
 * ## Deux réponses qui se croisent
 *
 * Le parent tape un code, l'écran demande un devis ; il en tape un second
 * avant que le premier ne revienne. Sans précaution, la réponse la plus lente
 * écraserait la plus récente et afficherait un total périmé — sur un écran de
 * paiement.
 *
 * Chaque demande porte donc un numéro, et seule la réponse de la dernière est
 * retenue. Les autres sont jetées en silence.
 */
export function useDevis(
  userId: string | null | undefined,
  lignes: LigneCommande[],
  reduitDeclare: boolean,
  codes: string[],
) {
  const [devis, setDevis] = useState<Devis | null>(null);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  /*
    Les entrées sont sérialisées pour servir de dépendance : deux tableaux de
    contenu identique ne sont pas le même objet, et une dépendance sur les
    tableaux eux-mêmes redemanderait un devis à chaque rendu.
  */
  const empreinte = JSON.stringify({ userId, lignes, reduitDeclare, codes });
  const dernier = useRef(0);

  const rafraichir = useCallback(async () => {
    const { userId: u, lignes: l, reduitDeclare: r, codes: c } = JSON.parse(empreinte);

    if (!u || !l || l.length === 0) {
      dernier.current += 1;          // Une réponse en vol ne doit plus écrire.
      setDevis(null);
      setErreur(null);
      setChargement(false);
      return;
    }

    const numero = (dernier.current += 1);
    setChargement(true);
    setErreur(null);

    try {
      const resultat = await chiffrer(u, l, r, c);
      if (numero !== dernier.current) return;   // Une demande plus récente a pris la main.
      setDevis(resultat);
    } catch (e) {
      if (numero !== dernier.current) return;
      /*
        Le devis échoue : on efface le total plutôt que de laisser l'ancien à
        l'écran. Un montant qui ne correspond plus au panier est pire que pas
        de montant du tout — le parent croirait payer celui-là.
      */
      setDevis(null);
      setErreur(e instanceof ErreurDeCommande ? e.message : String(e));
    } finally {
      if (numero === dernier.current) setChargement(false);
    }
  }, [empreinte]);

  useEffect(() => { void rafraichir(); }, [rafraichir]);

  return { devis, chargement, erreur, rafraichir };
}
